"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { fetchJson } from "@/lib/read-json";
import { isEstablishingSessionPath } from "@/lib/auth/nativeRestoreBoundary";
import {
  AuthReadUnreachable,
  isAuthReadTimeout,
  isUnreachableStatus,
  readWithBound,
} from "@/lib/auth/boundedSessionRead";
import { sanitizeNext } from "@/lib/sanitize-next";
import { clearAllCachedProposals } from "@/components/foundry/event-rooms/proposalContinuity";
import { isNative } from "@/lib/native/isNative";
import {
  restoreNativeSession,
  clearNativeSession,
  syncNativeSessionFromClient,
} from "@/lib/native/durableSession";

type AuthUser = {
  id: string;
  email?: string | null;
} | null;

type SessionResp = { ok: boolean; user?: any | null; error?: string; where?: string };
type LoginResp =
  | { ok: true; access_token: string; refresh_token: string; user?: { id: string; email?: string | null } | null }
  | { ok: false; error?: string; where?: string };

type RegisterResp =
  | { ok: true; access_token?: string; refresh_token?: string; needs_email_confirm?: boolean; user?: { id: string; email?: string | null } | null }
  | { ok: false; error?: string; where?: string };

type Ctx = {
  user: AuthUser;
  loading: boolean;
  /** R4-R4B-R1 — the session read did not come back in time. NOT "signed out". */
  unreachable: boolean;
  error: string | null;
  clearError: () => void;
  refresh: () => Promise<void>;
  login: (email: string, password: string, next?: string) => Promise<void>;
  register: (email: string, password: string, next?: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<Ctx | null>(null);

function safeJson(raw?: string | null): any | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function msgFromFetchFail(r: { raw?: string }) {
  const obj = safeJson(r.raw) as { error?: string; message?: string; where?: string } | null;
  return obj?.error ?? obj?.message ?? r.raw ?? "Request failed";
}

function assertOk<T extends { ok: boolean }>(
  r: { ok: boolean; status: number; json?: T; raw?: string },
  fallbackMsg: string
): asserts r is { ok: true; status: number; json: T } {
  if (!r.ok) throw new Error(msgFromFetchFail(r) || fallbackMsg);
}

let sessionInflight: Promise<SessionResp | null> | null = null;

/** ✅ 중복 GET 방지 + 실패 시 null */
async function fetchSessionOnce(): Promise<SessionResp | null> {
  if (!sessionInflight) {
    sessionInflight = (async () => {
      /*
        BOUNDED (Slice R4-R4B-R1). This was a bare `fetch` with no signal, and it is the request
        the whole app boot waits on. A stall left `loading` true forever and `/start` on its navy
        surface with nothing to press. The bound does not change what a REPLY means — only that
        silence eventually becomes an answer the caller can act on.
      */
      const r = await readWithBound((signal) =>
        fetchJson<SessionResp>(`/api/auth/session?_t=${Date.now()}`, { signal }),
      );
      if (!r.ok) {
        // 401(로그인 전)은 정상 흐름 → 에러로 취급하지 않고 비로그인 상태로 둠 (콘솔/UI 노이즈 제거)
        if (r.status === 401) return { ok: false as const };
        /*
          WE NEVER GOT AN ANSWER (Slice R4-R4B-R1).

          The bound above only ever caught silence. A request that fails INSTANTLY — airplane mode,
          no signal, a reset connection, a 502 from the edge — comes back through `fetchJson`'s
          catch as a resolved `{ ok: false, status: 0 }`, so it reached this line as an ordinary
          error and the caller set `user = null`. The launch then told a signed-in person they had
          no session and sent them to log in again, because their network dropped.
        */
        if (isUnreachableStatus(r.status)) throw new AuthReadUnreachable(r.status ?? 0);
        throw new Error(r.raw ?? "Session request failed");
      }
      // 200 + body.ok:false(세션 없음)도 그대로 반환, throw 안 함
      return r.json as SessionResp;
    })().finally(() => {
      sessionInflight = null;
    });
  }
  return sessionInflight;
}

/** ✅ 로그인 직후 쿠키 반영 레이스를 피하기 위한 재시도 */
async function fetchSessionAfterLogin(retries = 3, delayMs = 120): Promise<SessionResp | null> {
  for (let i = 0; i < retries; i++) {
    // 캐시된 inflight 무조건 끊고 새 GET 강제
    sessionInflight = null;
    const j = await fetchSessionOnce();
    if (j?.ok && j.user) return j;
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const localeForNext = pathname?.startsWith("/ko") ? "ko" : "en";
  // ✅ 로그인/공개 페이지에서는 세션 자동조회 자체를 하지 않음 (401 노이즈 제거)
  // pathname이 아직 null이면(SSR/첫 클라이언트 틱) 세션 조회를 건너뜀 — CI/E2E에서 폼이 막히지 않게 함
  const skipInitialSessionCheck =
    pathname == null ||
    pathname.includes("/bty/login") ||
    pathname.includes("/admin/login");

  // ✅ 랜딩·Arena·Center·로그인은 먼저 화면 띄우고 세션은 백그라운드에서 조회 (로딩에 막히지 않음)
  // pathname이 아직 null이면(라우터 준비 전) 공개 첫 페인트로 간주해 loading=false 유지
  const isPublicFirstPaint =
    pathname == null ||
    pathname === "/en" ||
    pathname === "/en/" ||
    pathname === "/ko" ||
    pathname === "/ko/" ||
    pathname.includes("/center") ||
    pathname.includes("/bty-arena") ||
    pathname.includes("/bty/login");

  const [user, setUser] = useState<AuthUser>(null);
  const [loading, setLoading] = useState(!isPublicFirstPaint);
  const [error, setError] = useState<string | null>(null);
  /*
    R4-R4B-R1 — WE COULD NOT REACH BTY. Deliberately NOT the same thing as `user === null`.
    `null` means the server told us there is no session; this means the server never told us
    anything. The boot surface renders an actionable retry from this, and NOTHING acts on it by
    signing anyone out, clearing a cookie or redirecting to login.
  */
  const [unreachable, setUnreachable] = useState(false);

  const clearError = useCallback(() => setError(null), []);

  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    setUnreachable(false);
    setLoading(true);
    try {
      let j = await fetchSessionOnce();

      // STEP A2 — native durable session restore (native only). A hard-kill right
      // after login can lose the WKWebView cookie before it flushes to disk, so the
      // gate returns unauthenticated even though the iOS Keychain still holds valid
      // session material. Rebuild from Keychain, then re-read the gate. Web is
      // unchanged: isNative() is false, so restoreNativeSession() no-ops → false.
      /*
        NEVER RESTORE AN OLD SESSION WHILE A NEW ONE IS BEING ESTABLISHED (Slice R1G).

        `AuthProvider` wraps the ROOT layout, so this runs on EVERY route — including
        `/{locale}/auth/callback`, whose entire job is to turn a fresh OAuth code into a session.
        On that route the gate is legitimately unauthenticated (the session does not exist yet),
        which is exactly the condition that triggers the restore below.

        That restore calls `supabase.auth.setSession(...)`, and auth-js's `_saveSession()` — the
        success path of setSession — calls removeItemAsync on `<storageKey>-code-verifier`. So the
        restore DESTROYS the PKCE verifier belonging to the sign-in currently in flight, racing the
        callback's own `exchangeCodeForSession`. Production showed exactly that:

          POST /auth/v1/token?grant_type=pkce → 400 bad_code_verifier
          "code challenge does not match previously saved code verifier"

        It is native-only because `isNative()` gates it — which is precisely why the web flow was
        unaffected while the installed app failed every time.

        Restoring a stale session on the callback route is redundant as well as destructive: the
        callback is about to establish the real one. Skip it there and let the exchange finish.
      */
      const onAuthCallback =
        isEstablishingSessionPath(typeof window !== "undefined" ? window.location.pathname : null);
      if ((!j || !j.ok || !j.user) && isNative() && !onAuthCallback) {
        const restored = await restoreNativeSession();
        if (restored) {
          sessionInflight = null;
          j = await fetchSessionOnce();
        }
      }

      if (!mounted.current) return;
      const authed = j?.ok ? (j.user ?? null) : null;
      setUser(authed);

      // STEP A2 — keep the Keychain copy synced to the client's latest (possibly
      // auto-rotated) tokens so a later hard-kill can still restore. Native only,
      // best-effort, non-blocking.
      if (authed && isNative()) void syncNativeSessionFromClient();
    } catch (e: any) {
      if (!mounted.current) return;
      /*
        A TIMEOUT IS NOT A "NO" (Slice R4-R4B-R1).

        The old branch set `user = null` for every failure, which for an expired bound would state
        that a legitimately signed-in person has no session — on the evidence that we failed to
        ask. Whatever we last knew is left standing, and the surface is told we could not reach
        BTY so it can offer a retry instead of a dead screen.
      */
      if (isAuthReadTimeout(e)) {
        setUnreachable(true);
        return;
      }
      setUser(null);
      setError(e?.message ?? String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipInitialSessionCheck) {
      if (mounted.current) setLoading(false);
      return;
    }
    if (isPublicFirstPaint) {
      if (mounted.current) setLoading(false);
      refresh();
      return;
    }
    refresh();
  }, [refresh, skipInitialSessionCheck, isPublicFirstPaint, pathname]);

  const login = useCallback(async (email: string, password: string, next?: string) => {
    setError(null);
    setLoading(true);

    try {
      // 1) 로그인 → 토큰 받기
      const r1 = await fetchJson<LoginResp>("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!r1.ok) throw new Error(msgFromFetchFail(r1));
      if (!r1.json || (r1.json as any).ok !== true) throw new Error((r1.json as any)?.error ?? "Login failed");

      const data = r1.json as Extract<LoginResp, { ok: true }>;
      const access_token = data.access_token;
      const refresh_token = data.refresh_token;

      if (!access_token || !refresh_token) throw new Error("missing tokens");

      // 2) 토큰으로 쿠키 세션 세팅 (⭐ 여기서 Set-Cookie가 내려와야 함)
      const r2 = await fetchJson<{ ok: boolean; error?: string }>("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token, refresh_token }),
      });
      assertOk(r2 as any, "Session POST failed");
      if (!r2.json?.ok) throw new Error(r2.json?.error ?? "세션 생성 실패(쿠키 미설정)");

      // ✅ 캐시 리셋하여 새로운 GET 요청 강제
      sessionInflight = null;

      // 3) 쿠키 반영될 때까지 GET 재시도 (반드시 200 {ok:true,user} 받을 때까지)
      const j = await fetchSessionAfterLogin();
      if (!j?.ok || !j.user) throw new Error("세션 생성 실패(쿠키 미설정)");

      if (mounted.current) setUser(j.user);

      // 4) 이동 - next 검증 후 서버 after-login을 거쳐 리다이렉트 (redirect loop 방지)
      const nextSafe = sanitizeNext(next, localeForNext);
      window.location.assign(`/api/auth/after-login?next=${encodeURIComponent(nextSafe)}`);
    } catch (e: any) {
      if (mounted.current) {
        setUser(null);
        setError(e?.message ?? String(e));
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [localeForNext]);

  const register = useCallback(async (email: string, password: string, next?: string) => {
    setError(null);
    setLoading(true);

    try {
      // 1) 회원가입 → (설정에 따라) 토큰이 올 수도/안 올 수도 있음
      const r1 = await fetchJson<RegisterResp>("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!r1.ok) throw new Error(msgFromFetchFail(r1));
      if (!r1.json || (r1.json as any).ok !== true) throw new Error((r1.json as any)?.error ?? "Register failed");

      const data = r1.json as Extract<RegisterResp, { ok: true }>;

      // 이메일 확인이 필요한 경우: 여기서 종료 (UI 메시지)
      if (data.needs_email_confirm) {
        if (mounted.current) {
          setUser(null);
          setError("이메일 확인이 필요합니다. 받은 메일에서 인증 후 다시 로그인해주세요.");
        }
        return;
      }

      const access_token = data.access_token;
      const refresh_token = data.refresh_token;

      if (!access_token || !refresh_token) {
        // needs_email_confirm가 아닌데 토큰이 없다면 서버 설정/응답 이상
        throw new Error("missing tokens");
      }

      // 2) 토큰으로 쿠키 세션 세팅
      const r2 = await fetchJson<{ ok: boolean; error?: string }>("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token, refresh_token }),
      });
      assertOk(r2 as any, "Session POST failed");
      if (!r2.json?.ok) throw new Error(r2.json?.error ?? "세션 생성 실패(쿠키 미설정)");

      // ✅ 캐시 리셋하여 새로운 GET 요청 강제
      sessionInflight = null;

      // 3) 쿠키 반영될 때까지 GET 재시도 (반드시 200 {ok:true,user} 받을 때까지)
      const j = await fetchSessionAfterLogin();
      if (!j?.ok || !j.user) throw new Error("세션 생성 실패(쿠키 미설정)");

      if (mounted.current) setUser(j.user);

      // 4) 이동 - next 검증 후 서버 after-login을 거쳐 리다이렉트 (redirect loop 방지)
      const nextSafe = sanitizeNext(next, localeForNext);
      window.location.assign(`/api/auth/after-login?next=${encodeURIComponent(nextSafe)}`);
    } catch (e: any) {
      if (mounted.current) {
        setUser(null);
        setError(e?.message ?? String(e));
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [localeForNext]);

  const logout = useCallback(async () => {
    // 서버에 logout route가 있으면 호출해도 좋지만, 지금은 쿠키 기반이라
    // supabase signOut route를 만들거나, 쿠키 삭제 route를 만들기 전까지는
    // 사용자 상태만 비우고 refresh로 동기화하는 방식
    // STEP A2 — clear the iOS Keychain session BEFORE refresh(), so the restore path
    // does not silently re-authenticate the user on logout. No-op on web.
    await clearNativeSession();
    /*
      Unfinished training content is for the person who generated it (Slice 3.2L-R11.4K-R2).
      Proposal continuity is shared across tabs so a Host never has to hunt for the right
      one; leaving the device ends it.
    */
    clearAllCachedProposals();
    setUser(null);
    sessionInflight = null;
    await refresh();
    window.location.assign("/bty/login");
  }, [refresh]);

  const value = useMemo<Ctx>(
    () => ({ user, loading, error, unreachable, clearError, refresh, login, register, logout }),
    [user, loading, error, unreachable, clearError, refresh, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
